import { put, select, call } from 'redux-saga/effects';
import { actions as toastrActions } from 'react-redux-toastr';

import api, { apiLogin } from 'services/api';
import { viagensAPI, cargasAPI, clientesAPI, fornecedoresAPI, accountsPayableAPI, accountsReceivableAPI } from 'services/apiRest';

import ConvocationsActions from '../ducks/convocations';
import ShortUniqueId from 'short-unique-id';
// import AuthActions from '../ducks/auth';

// convocationsWatcher removido - agora usamos APIs REST
// Para atualizações em tempo real, pode-se implementar polling ou WebSockets no futuro
export function* convocationsWatcher() {
  // Watcher desabilitado - usando APIs REST agora
  // As atualizações são feitas via getConvocations quando necessário
  while (true) {
    // Aguardar indefinidamente (watcher desabilitado)
    yield new Promise(() => {}); // Nunca resolve
  }
}

export function* getConvocations() {
  try {
    // Usar API REST de cargas ao invés de viagens
    const cargas = yield call(cargasAPI.listar, {});
    
    // Filtrar cargas canceladas
    const cargasFiltradas = cargas.filter((carga) => carga.status !== 'canceled');
    
    // Remover duplicatas baseado no ID
    const cargasUnicas = cargasFiltradas.filter((carga, index, self) =>
      index === self.findIndex((c) => c.id === carga.id)
    );
    
    // Converter formato de cargas para o formato esperado pelo frontend
    const convocations = cargasUnicas.map((carga) => {
      // Converter created_at de ISO string para formato Firebase (seconds)
      const created_at = carga.created_at 
        ? { seconds: Math.round(new Date(carga.created_at).getTime() / 1000) }
        : { seconds: Math.round(Date.now() / 1000) };
      
      // Converter validity se existir
      const validity = carga.validity
        ? { seconds: Math.round(new Date(carga.validity).getTime() / 1000) }
        : null;

      return {
        ...carga,
        created_at,
        validity,
      };
    });

    console.log(`✅ [getConvocations] ${convocations.length} carga(s) carregada(s)`);
    yield put(ConvocationsActions.getConvocationsSuccess(convocations));
  } catch (err) {
    console.error('❌ [getConvocations] Erro ao buscar cargas:', err);
    yield put(ConvocationsActions.getConvocationsFailure(err));
  }
}

export function* addConvocation({ convocation, closeModal }) {
  try {
    const userActive = yield select((state) => state.auth.userActive);

    // Preparar dados para a API REST (remover campos específicos de viagem)
    let data = {
      ...convocation,
      status: 'initial',
    };

    // Remover campos que não pertencem à carga
    delete data.delivery_viagens; // Será tratado separadamente
    delete data.invoices; // Invoices são de viagens, não de cargas
    delete data.cte_type;
    delete data.cte_id;
    delete data.mdfe_id;
    delete data.serie_cte;
    delete data.numero_cte;
    delete data.chave_nfe;
    delete data.status_mdfe;
    delete data.label_status;

    // Converter validity se for objeto com seconds
    if (data.validity && typeof data.validity === 'object' && data.validity.seconds) {
      data.validity = new Date(data.validity.seconds * 1000).toISOString();
    } else if (data.validity instanceof Date) {
      data.validity = data.validity.toISOString();
    }

    // Extrair IDs das viagens selecionadas (se houver)
    const viagensIds = convocation.delivery_viagens 
      ? convocation.delivery_viagens.map(v => v.id).filter(id => id)
      : [];
    
    // Criar CARGA via API REST (não viagem)
    const cargaCriada = yield call(cargasAPI.criar, data);

    // Relacionar viagens à carga se houver viagens selecionadas
    if (viagensIds.length > 0) {
      try {
        yield call(cargasAPI.relacionarViagens, cargaCriada.id, viagensIds);
        console.log(`✅ ${viagensIds.length} viagem(ns) relacionada(s) à carga ${cargaCriada.id}`);
      } catch (error) {
        console.error('⚠️ Erro ao relacionar viagens à carga:', error);
        // Não falhar a criação da carga se o relacionamento falhar
      }
    }

    // Converter para formato esperado pelo frontend
    const created_at = cargaCriada.created_at 
      ? { seconds: Math.round(new Date(cargaCriada.created_at).getTime() / 1000) }
      : { seconds: Math.round(Date.now() / 1000) };
    
    const validity = cargaCriada.validity
      ? { seconds: Math.round(new Date(cargaCriada.validity).getTime() / 1000) }
      : null;

    const dataFormatted = {
      ...cargaCriada,
      created_at,
      validity,
    };

    yield put(ConvocationsActions.addConvocationSuccess(dataFormatted));

    // Gerar títulos após criar a carga
    try {
      yield call(gerarTitulosCarga, cargaCriada, convocation);
    } catch (error) {
      console.error('⚠️ Erro ao gerar títulos da carga:', error);
      // Não falhar a criação da carga se a geração de títulos falhar
      yield put(
        toastrActions.add({
          type: 'warning',
          title: 'Aviso',
          message: 'Carga criada, mas houve erro ao gerar títulos. Verifique manualmente.',
        })
      );
    }

    yield put(
      toastrActions.add({
        type: 'success',
        title: 'CARGA',
        message: 'Registrada com sucesso!',
      })
    );

    if (closeModal) closeModal();
  } catch (err) {
    console.error('Erro ao cadastrar carga:', err);
    yield put(
      toastrActions.add({
        type: 'error',
        title: 'Erro ao cadastrar carga',
        message: err.message || 'Tente novamente',
      })
    );

    yield put(ConvocationsActions.addConvocationFailure(err));
  }
}

/**
 * Função para gerar os 3 títulos após criar uma carga
 */
function* gerarTitulosCarga(cargaCriada, convocation) {
  try {
    console.log('🔄 [gerarTitulosCarga] Iniciando geração de títulos para carga:', cargaCriada.id);

    // Buscar cliente pelo CNPJ
    const cnpjLimpo = (convocation.cnpj || '').replace(/\D/g, '');
    if (!cnpjLimpo) {
      console.warn('⚠️ [gerarTitulosCarga] CNPJ não informado, não é possível gerar títulos');
      return;
    }

    const clientes = yield call(clientesAPI.listar, { cpfCnpj: cnpjLimpo });
    if (!clientes || clientes.length === 0) {
      console.warn('⚠️ [gerarTitulosCarga] Cliente não encontrado para CNPJ:', cnpjLimpo);
      return;
    }
    const cliente = clientes[0];

    // Buscar fornecedor (motorista) pelo CPF do primeiro condutor
    let fornecedor = null;
    if (convocation.condutores && convocation.condutores.length > 0) {
      const primeiroCondutor = convocation.condutores[0];
      if (primeiroCondutor.cpf) {
        const cpfLimpo = primeiroCondutor.cpf.replace(/\D/g, '');
        const fornecedores = yield call(fornecedoresAPI.listar, { cpfCnpj: cpfLimpo });
        if (fornecedores && fornecedores.length > 0) {
          fornecedor = fornecedores[0];
        }
      }
    }

    if (!fornecedor) {
      console.warn('⚠️ [gerarTitulosCarga] Fornecedor (motorista) não encontrado');
      return;
    }

    // Calcular valores
    const freightValue = parseFloat(convocation.freight_value || 0);
    const taxaEmissao = parseFloat(cliente.taxaEmissao || 0) / 100; // Converter percentual para decimal
    const valorTaxaEmissao = freightValue * taxaEmissao;
    const valorContaReceber = freightValue + valorTaxaEmissao;
    const adiantamentoMotorista = parseFloat(convocation.adiantamento_motorista || 0);
    const freteMotorista = parseFloat(convocation.value || 0);
    const valorPedagio = parseFloat(convocation.toll_value || 0);
    const valorContaPagarFrete = freteMotorista + valorPedagio;

    // Calcular datas de vencimento baseadas na data de criação da carga
    const dataCriacaoCarga = cargaCriada.created_at 
      ? new Date(cargaCriada.created_at) 
      : new Date();
    dataCriacaoCarga.setHours(0, 0, 0, 0);
    
    // Título 1 - Contas a Receber: d+1 + dias (não considera o dia de criação)
    const diasVencimento = parseInt(cliente.prazoPagamento || 0);
    const dataVencimentoReceber = new Date(dataCriacaoCarga);
    dataVencimentoReceber.setDate(dataCriacaoCarga.getDate() + 1 + diasVencimento); // d+1 + dias

    // Título 2 - Contas a Pagar (Adiantamento): D + 3 (da data de criação)
    const dataVencimentoAdiantamento = new Date(dataCriacaoCarga);
    dataVencimentoAdiantamento.setDate(dataCriacaoCarga.getDate() + 3);

    // Título 3 - Contas a Pagar (Frete): usando prazo de pagamento do cliente (não considera dia de criação)
    const prazoPagamentoCliente = parseInt(cliente.prazoPagamento || 0);
    const dataVencimentoFrete = new Date(dataCriacaoCarga);
    dataVencimentoFrete.setDate(dataCriacaoCarga.getDate() + prazoPagamentoCliente);

    // Obter número da carga (usar código se disponível, senão usar ID)
    const numeroCarga = cargaCriada.codigo || cargaCriada.id || `Carga ${cargaCriada.id}`;

    // Título 1 - Contas a Receber
    if (valorContaReceber > 0) {
      const contaReceber = {
        description: numeroCarga,
        customer: cliente.nome,
        customerId: cliente.id,
        value: valorContaReceber,
        dueDate: dataVencimentoReceber.toISOString().split('T')[0],
        status: 'pending',
        naturezaId: convocation.natureza_frete_id || null,
        formaPagamento: cliente.formaPagamento || null,
      };
      yield call(accountsReceivableAPI.criar, contaReceber);
      console.log('✅ [gerarTitulosCarga] Conta a Receber criada:', contaReceber);
    }

    // Título 2 - Contas a Pagar (Adiantamento Motorista)
    if (adiantamentoMotorista > 0 && convocation.natureza_adiantamento_id) {
      const contaPagarAdiantamento = {
        description: `Adiantamento Motorista - ${numeroCarga}`,
        supplier: fornecedor.nome,
        supplierId: fornecedor.id,
        value: adiantamentoMotorista,
        dueDate: dataVencimentoAdiantamento.toISOString().split('T')[0],
        status: 'pending',
        naturezaId: convocation.natureza_adiantamento_id,
        formaPagamento: fornecedor.formaPagamento || null,
      };
      yield call(accountsPayableAPI.criar, contaPagarAdiantamento);
      console.log('✅ [gerarTitulosCarga] Conta a Pagar (Adiantamento) criada:', contaPagarAdiantamento);
    }

    // Título 3 - Contas a Pagar (Frete Motorista)
    if (valorContaPagarFrete > 0 && convocation.natureza_frete_id) {
      const contaPagarFrete = {
        description: `Frete Motorista - ${numeroCarga}`,
        supplier: fornecedor.nome,
        supplierId: fornecedor.id,
        value: valorContaPagarFrete,
        dueDate: dataVencimentoFrete.toISOString().split('T')[0],
        status: 'pending',
        naturezaId: convocation.natureza_frete_id,
        formaPagamento: cliente.formaPagamento || null,
      };
      yield call(accountsPayableAPI.criar, contaPagarFrete);
      console.log('✅ [gerarTitulosCarga] Conta a Pagar (Frete) criada:', contaPagarFrete);
    }

    console.log('✅ [gerarTitulosCarga] Todos os títulos foram gerados com sucesso');
  } catch (error) {
    console.error('❌ [gerarTitulosCarga] Erro ao gerar títulos:', error);
    throw error;
  }
}

/**
 * Função para atualizar os 3 títulos após editar uma carga
 */
function* atualizarTitulosCarga(cargaAtualizada, convocation) {
  try {
    console.log('🔄 [atualizarTitulosCarga] Iniciando atualização de títulos para carga:', cargaAtualizada.id);

    // Buscar cliente pelo CNPJ
    const cnpjLimpo = (convocation.cnpj || '').replace(/\D/g, '');
    if (!cnpjLimpo) {
      console.warn('⚠️ [atualizarTitulosCarga] CNPJ não informado, não é possível atualizar títulos');
      return;
    }

    const clientes = yield call(clientesAPI.listar, { cpf_cnpj: cnpjLimpo });
    if (!clientes || clientes.length === 0) {
      console.warn('⚠️ [atualizarTitulosCarga] Cliente não encontrado para CNPJ:', cnpjLimpo);
      return;
    }
    const cliente = clientes[0];

    // Buscar fornecedor (motorista) pelo CPF do primeiro condutor
    let fornecedor = null;
    if (convocation.condutores && convocation.condutores.length > 0) {
      const primeiroCondutor = convocation.condutores[0];
      if (primeiroCondutor.cpf) {
        const cpfLimpo = primeiroCondutor.cpf.replace(/\D/g, '');
        const fornecedores = yield call(fornecedoresAPI.listar, { cpf_cnpj: cpfLimpo });
        if (fornecedores && fornecedores.length > 0) {
          fornecedor = fornecedores[0];
        }
      }
    }

    if (!fornecedor) {
      console.warn('⚠️ [atualizarTitulosCarga] Fornecedor (motorista) não encontrado');
      return;
    }

    // Calcular valores
    const freightValue = parseFloat(convocation.freight_value || 0);
    const taxaEmissao = parseFloat(cliente.taxaEmissao || 0) / 100; // Converter percentual para decimal
    const valorTaxaEmissao = freightValue * taxaEmissao;
    const valorContaReceber = freightValue + valorTaxaEmissao;
    const adiantamentoMotorista = parseFloat(convocation.adiantamento_motorista || 0);
    const freteMotorista = parseFloat(convocation.value || 0);
    const valorPedagio = parseFloat(convocation.toll_value || 0);
    const valorContaPagarFrete = freteMotorista + valorPedagio;

    // Calcular datas de vencimento baseadas na data de criação da carga
    const dataCriacaoCarga = cargaAtualizada.created_at 
      ? new Date(cargaAtualizada.created_at) 
      : new Date();
    dataCriacaoCarga.setHours(0, 0, 0, 0);
    
    // Título 1 - Contas a Receber: d+1 + dias (não considera o dia de criação)
    const diasVencimento = parseInt(cliente.prazoPagamento || 0);
    const dataVencimentoReceber = new Date(dataCriacaoCarga);
    dataVencimentoReceber.setDate(dataCriacaoCarga.getDate() + 1 + diasVencimento); // d+1 + dias

    // Título 2 - Contas a Pagar (Adiantamento): D + 3 (da data de criação)
    const dataVencimentoAdiantamento = new Date(dataCriacaoCarga);
    dataVencimentoAdiantamento.setDate(dataCriacaoCarga.getDate() + 3);

    // Título 3 - Contas a Pagar (Frete): usando prazo de pagamento do cliente (não considera dia de criação)
    const prazoPagamentoCliente = parseInt(cliente.prazoPagamento || 0);
    const dataVencimentoFrete = new Date(dataCriacaoCarga);
    dataVencimentoFrete.setDate(dataCriacaoCarga.getDate() + prazoPagamentoCliente);

    // Obter número da carga (usar código se disponível, senão usar ID)
    const numeroCarga = cargaAtualizada.codigo || cargaAtualizada.id || `Carga ${cargaAtualizada.id}`;

    // Buscar títulos existentes pela descrição
    const todasContasReceber = yield call(accountsReceivableAPI.listar, {});
    const todasContasPagar = yield call(accountsPayableAPI.listar, {});

    // Título 1 - Contas a Receber
    if (valorContaReceber > 0) {
      // Buscar título existente pela descrição (número da carga)
      // Pode ser o código, ID ou formato "Carga {id}"
      const tituloReceberExistente = todasContasReceber.find(
        (titulo) => 
          titulo.description === numeroCarga || 
          titulo.description === String(cargaAtualizada.id) ||
          titulo.description === cargaAtualizada.codigo ||
          titulo.description === `Carga ${cargaAtualizada.id}`
      );

      const dadosContaReceber = {
        description: numeroCarga,
        customer: cliente.nome,
        customerId: cliente.id,
        value: valorContaReceber,
        dueDate: dataVencimentoReceber.toISOString().split('T')[0],
        status: tituloReceberExistente?.status || 'pending', // Manter status existente se houver
        naturezaId: convocation.natureza_frete_id || null,
        formaPagamento: cliente.formaPagamento || null,
      };

      if (tituloReceberExistente) {
        // Atualizar título existente
        yield call(accountsReceivableAPI.atualizar, tituloReceberExistente.id, dadosContaReceber);
        console.log('✅ [atualizarTitulosCarga] Conta a Receber atualizada:', tituloReceberExistente.id);
      } else {
        // Criar novo título se não existir
        yield call(accountsReceivableAPI.criar, dadosContaReceber);
        console.log('✅ [atualizarTitulosCarga] Conta a Receber criada (não existia):', dadosContaReceber);
      }
    }

    // Título 2 - Contas a Pagar (Adiantamento Motorista)
    if (adiantamentoMotorista > 0 && convocation.natureza_adiantamento_id) {
      const descricaoAdiantamento = `Adiantamento Motorista - ${numeroCarga}`;
      const tituloAdiantamentoExistente = todasContasPagar.find(
        (titulo) => 
          titulo.description === descricaoAdiantamento || 
          (titulo.description && titulo.description.includes('Adiantamento Motorista') && (
            titulo.description.includes(numeroCarga) ||
            titulo.description.includes(String(cargaAtualizada.id)) ||
            titulo.description.includes(cargaAtualizada.codigo || '')
          ))
      );

      const dadosContaPagarAdiantamento = {
        description: descricaoAdiantamento,
        supplier: fornecedor.nome,
        supplierId: fornecedor.id,
        value: adiantamentoMotorista,
        dueDate: dataVencimentoAdiantamento.toISOString().split('T')[0],
        status: tituloAdiantamentoExistente?.status || 'pending', // Manter status existente se houver
        naturezaId: convocation.natureza_adiantamento_id,
        formaPagamento: fornecedor.formaPagamento || null,
      };

      if (tituloAdiantamentoExistente) {
        // Atualizar título existente
        yield call(accountsPayableAPI.atualizar, tituloAdiantamentoExistente.id, dadosContaPagarAdiantamento);
        console.log('✅ [atualizarTitulosCarga] Conta a Pagar (Adiantamento) atualizada:', tituloAdiantamentoExistente.id);
      } else {
        // Criar novo título se não existir
        yield call(accountsPayableAPI.criar, dadosContaPagarAdiantamento);
        console.log('✅ [atualizarTitulosCarga] Conta a Pagar (Adiantamento) criada (não existia):', dadosContaPagarAdiantamento);
      }
    }

    // Título 3 - Contas a Pagar (Frete Motorista)
    if (valorContaPagarFrete > 0 && convocation.natureza_frete_id) {
      const descricaoFrete = `Frete Motorista - ${numeroCarga}`;
      const tituloFreteExistente = todasContasPagar.find(
        (titulo) => 
          titulo.description === descricaoFrete || 
          (titulo.description && titulo.description.includes('Frete Motorista') && (
            titulo.description.includes(numeroCarga) ||
            titulo.description.includes(String(cargaAtualizada.id)) ||
            titulo.description.includes(cargaAtualizada.codigo || '')
          ))
      );

      const dadosContaPagarFrete = {
        description: descricaoFrete,
        supplier: fornecedor.nome,
        supplierId: fornecedor.id,
        value: valorContaPagarFrete,
        dueDate: dataVencimentoFrete.toISOString().split('T')[0],
        status: tituloFreteExistente?.status || 'pending', // Manter status existente se houver
        naturezaId: convocation.natureza_frete_id,
        formaPagamento: cliente.formaPagamento || null,
      };

      if (tituloFreteExistente) {
        // Atualizar título existente
        yield call(accountsPayableAPI.atualizar, tituloFreteExistente.id, dadosContaPagarFrete);
        console.log('✅ [atualizarTitulosCarga] Conta a Pagar (Frete) atualizada:', tituloFreteExistente.id);
      } else {
        // Criar novo título se não existir
        yield call(accountsPayableAPI.criar, dadosContaPagarFrete);
        console.log('✅ [atualizarTitulosCarga] Conta a Pagar (Frete) criada (não existia):', dadosContaPagarFrete);
      }
    }

    console.log('✅ [atualizarTitulosCarga] Todos os títulos foram atualizados com sucesso');
  } catch (error) {
    console.error('❌ [atualizarTitulosCarga] Erro ao atualizar títulos:', error);
    throw error;
  }
}

export function* updateConvocation({ convocation, closeModal }) {
  try {
    const userActive = yield select((state) => state.auth.userActive);

    // Preparar dados para a API REST
    let data = {
      ...convocation,
    };

    // Remover campos que não devem ser atualizados
    delete data.created_at;
    delete data.delivery_viagens; // Será tratado separadamente
    delete data.invoices;
    delete data.cte_type;
    delete data.cte_id;
    delete data.mdfe_id;
    delete data.serie_cte;
    delete data.numero_cte;
    delete data.chave_nfe;
    delete data.status_mdfe;
    delete data.label_status;

    // Converter validity se for objeto com seconds
    if (data.validity && typeof data.validity === 'object' && data.validity.seconds) {
      data.validity = new Date(data.validity.seconds * 1000).toISOString();
    } else if (data.validity instanceof Date) {
      data.validity = data.validity.toISOString();
    }

    // Extrair IDs das viagens selecionadas (se houver)
    const viagensIds = convocation.delivery_viagens 
      ? convocation.delivery_viagens.map(v => v.id).filter(id => id)
      : [];

    // Atualizar CARGA via API REST (não viagem)
    const cargaAtualizada = yield call(cargasAPI.atualizar, convocation.id, data);

    // Relacionar viagens à carga se houver viagens selecionadas
    if (viagensIds.length > 0) {
      try {
        yield call(cargasAPI.relacionarViagens, cargaAtualizada.id, viagensIds);
        console.log(`✅ ${viagensIds.length} viagem(ns) relacionada(s) à carga ${cargaAtualizada.id}`);
      } catch (error) {
        console.error('⚠️ Erro ao relacionar viagens à carga:', error);
        // Não falhar a atualização da carga se o relacionamento falhar
      }
    }

    // Converter para formato esperado pelo frontend
    const created_at = convocation.created_at || 
      (cargaAtualizada.created_at 
        ? { seconds: Math.round(new Date(cargaAtualizada.created_at).getTime() / 1000) }
        : { seconds: Math.round(Date.now() / 1000) });
    
    const validity = cargaAtualizada.validity
      ? { seconds: Math.round(new Date(cargaAtualizada.validity).getTime() / 1000) }
      : (data.validity ? { seconds: Math.round(new Date(data.validity).getTime() / 1000) } : null);

    const dataFormatted = {
      ...cargaAtualizada,
      created_at,
      validity,
    };

    yield put(ConvocationsActions.updateConvocationSuccess(dataFormatted));
    yield put(ConvocationsActions.getConvocationsRequest());

    // Atualizar títulos financeiros após atualizar a carga
    try {
      yield call(atualizarTitulosCarga, cargaAtualizada, convocation);
      console.log('✅ [updateConvocation] Títulos atualizados com sucesso');
    } catch (error) {
      console.error('⚠️ [updateConvocation] Erro ao atualizar títulos da carga:', error);
      // Não falhar a atualização da carga se a atualização de títulos falhar
      yield put(
        toastrActions.add({
          type: 'warning',
          title: 'Carga atualizada',
          message: 'Carga atualizada, mas houve erro ao atualizar títulos. Verifique manualmente.',
        })
      );
    }

    yield put(
      toastrActions.add({
        type: 'success',
        title: 'CARGA',
        message: 'Atualizada com sucesso!',
      })
    );
    if (closeModal) closeModal();
  } catch (err) {
    console.error('Erro ao atualizar carga:', err);
    yield put(
      toastrActions.add({
        type: 'error',
        title: 'Erro ao atualizar a carga',
        message: err.message || 'Tente novamente',
      })
    );

    yield put(ConvocationsActions.updateConvocationFailure(err));
  }
}

export function* getTemporaryLink({ convocationId, invoiceId }) {
  try {
    let resp = yield api
      .firestore()
      .collection('temporary')
      .where('convocation_id', '==', convocationId)
      .get();

    const todayDate = new Date();
    let respFiltered;

    if (invoiceId) {
      respFiltered = resp.docs
        .filter(
          (obj) =>
            obj.data().due_date > todayDate.getTime() &&
            obj.data().client_id_invoice === invoiceId
        )
        .map((obj) => (obj = obj.data()));
    } else {
      respFiltered = resp.docs
        .filter((obj) => obj.data().due_date > todayDate.getTime())
        .map((obj) => (obj = obj.data()));
    }

    if (
      resp.empty ||
      respFiltered.length == 0 ||
      (!invoiceId && respFiltered.pop().client_id_invoice)
    ) {
      console.log('EMPTY');
      const userActive = yield select((state) => state.auth.userActive);
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 30);
      const uniqueId = new ShortUniqueId({ length: 6 });
      const uid = uniqueId();

      yield api.firestore().collection('temporary').doc(uid).set({
        id: uid,
        user_id: userActive.id,
        convocation_id: convocationId,
        due_date: dueDate.getTime(),
      });

      yield put(
        ConvocationsActions.setTemporaryLink({
          id: uid,
          user_id: userActive.id,
          convocation_id: convocationId,
          due_date: dueDate.getTime(),
        })
      );
    } else {
      console.log('NOT_EMPTY');
      yield put(ConvocationsActions.setTemporaryLink(respFiltered.pop()));
    }
  } catch (err) {
    console.log(err);

    yield put(
      toastrActions.add({
        type: 'error',
        title: 'Erro ao criar link de compartilhamento',
        message: 'Tente novamente',
      })
    );
  }
}

export function* updateLocation({
  fixedLocations,
  locations,
  selectedConvocation,
  closeModal,
}) {
  try {
    yield api
      .firestore()
      .collection('location')
      .doc(selectedConvocation.id)
      .set(
        {
          user_location: JSON.parse(JSON.stringify(fixedLocations)),
          bkup_user_location: JSON.parse(JSON.stringify(locations)),
        },
        { merge: true }
      );

    yield put(
      toastrActions.add({
        type: 'success',
        title: 'Location',
        message: 'Atualizada com sucesso!',
      })
    );
    if (closeModal) closeModal();
  } catch (err) {
    console.log(err);

    yield put(
      toastrActions.add({
        type: 'error',
        title: 'Erro ao atualizar a location',
        message: 'Tente novamente',
      })
    );
  }
}

export function* removeConvocation({ id }) {
  try {
    console.log(`🔵 [removeConvocation] Excluindo carga ${id}`);
    // Usar API REST de cargas ao invés de viagens
    yield call(cargasAPI.deletar, id);

    console.log(`✅ [removeConvocation] Carga ${id} excluída com sucesso`);
    yield put(ConvocationsActions.removeConvocationSuccess());
    yield put(ConvocationsActions.setAcceptedDelete());
    yield put(ConvocationsActions.getConvocationsRequest());
  } catch (err) {
    console.error('❌ [removeConvocation] Erro ao remover carga:', err);
    yield put(ConvocationsActions.removeConvocationFailure(err));
    yield put(ConvocationsActions.setErrorDelete());
  }
}

export function* sendMail({ file }) {
  try {
    const { convocationActive } = yield select((state) => state.convocations);

    let link;
    if (!convocationActive?.qrcode_url) {
      link = yield uploadQrcode(convocationActive.id, file);
    }

    const sendMailCollect = yield api.functions().httpsCallable('sendMail');
    sendMailCollect({
      dest: convocationActive.collect_corresponding_email,
      link: link || convocationActive?.qrcode_url,
    })
      .then((result) => {
        // Read result of the Cloud Function.
        console.log('sanitizedMessageCollect', result);
      })
      .catch((error) => {
        // Getting the Error details.
        if (error) {
          put(
            toastrActions.add({
              type: 'error',
              title: 'Erro ao enviar o e-mail',
              message: 'Tente novamente mais tarde.',
            })
          );
        }
        // ...
      });

    const sendMailDelivery = yield api.functions().httpsCallable('sendMail');
    sendMailDelivery({
      dest: convocationActive.delivery_corresponding_email,
      link: link || convocationActive?.qrcode_url,
    })
      .then((result) => {
        // Read result of the Cloud Function.
        console.log('sanitizedMessageDelivery', result);
      })
      .catch((error) => {
        // Getting the Error details.
        if (error) {
          put(
            toastrActions.add({
              type: 'error',
              title: 'Erro ao enviar o e-mail',
              message: 'Tente novamente mais tarde.',
            })
          );
        }
        // ...
      });

    yield put(
      toastrActions.add({
        type: 'success',
        title: 'E-mail',
        message: 'Enviado com sucesso!',
      })
    );

    yield put(ConvocationsActions.putSendMailSuccess());
  } catch (err) {
    yield put(
      toastrActions.add({
        type: 'error',
        title: 'Erro ao enviar o e-mail',
        message: 'Tente novamente mais tarde.',
      })
    );
    yield put(ConvocationsActions.putSendMailFailure(err));
  }
}

export function* uploadQrcode(uid, file) {
  // Extract image extension
  const filename = `qrcode.png`;

  yield apiLogin
    .storage()
    .ref(`qrcodes/${uid}/${filename}`)
    .putString(file, 'data_url');

  const uri = yield apiLogin
    .storage()
    .ref(`qrcodes/${uid}/${filename}`)
    .getDownloadURL();

  yield api
    .firestore()
    .collection('convocations')
    .doc(uid)
    .update({ qrcode_url: uri });

  return uri;
}
